import { useCallback, useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage, API } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, Save, RotateCcw, FileDown, Scale } from "lucide-react";
import { toast } from "sonner";

/**
 * R153 — Delinquency letter templates.
 *
 * Per-state + per-stage. Admin can override the state defaults (FL/TX/CA/GA)
 * on a per-property basis. Preview renders a branded PDF letter.
 */
export default function DelinquencyLettersPage() {
  const { properties, propertyId } = useProperty();
  const [scope, setScope] = useState(propertyId || properties[0]?.id || "");
  const [state, setState] = useState("FL");
  const [defaults, setDefaults] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [selectedStage, setSelectedStage] = useState("gentle_reminder");
  const [busy, setBusy] = useState(false);
  const [previewCtx, setPreviewCtx] = useState({
    unit: "101", resident_name: "Jane Owner",
    amount: 425, late_fee: 25, total_due: 450,
    days_past_due: 45, due_date: "2026-01-01",
  });

  useEffect(() => {
    if (properties.length && !scope) setScope(properties[0].id);
  }, [properties, scope]);

  useEffect(() => {
    api.get("/admin/delinquency/letters/defaults").then(({ data }) => setDefaults(data));
  }, []);

  const loadOverrides = useCallback(async () => {
    if (!scope) return;
    try {
      const { data } = await api.get("/admin/delinquency/letters", { params: { property_id: scope } });
      const map = {};
      (data.items || []).forEach((t) => {
        map[`${t.state}::${t.stage_key}`] = t;
      });
      setOverrides(map);
    } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/DelinquencyLettersPage.jsx: swallowed', err); }
  }, [scope]);

  useEffect(() => { loadOverrides(); }, [loadOverrides]);

  const stageTemplate = useMemo(() => {
    const key = `${state}::${selectedStage}`;
    if (overrides[key]) return { subject: overrides[key].subject, body: overrides[key].body, overridden: true };
    const st = defaults?.letters?.[state] || defaults?.letters?.FL || {};
    const d = st?.[selectedStage] || { subject: "", body: "" };
    return { subject: d.subject, body: d.body, overridden: false };
  }, [state, selectedStage, defaults, overrides]);

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  useEffect(() => {
    setSubject(stageTemplate.subject);
    setBody(stageTemplate.body);
  }, [stageTemplate]);

  const save = async () => {
    if (!scope) return toast.error("Pick a property");
    setBusy(true);
    try {
      await api.put("/admin/delinquency/letters", {
        property_id: scope, state, stage_key: selectedStage,
        subject, body,
      });
      toast.success("Template saved");
      await loadOverrides();
    } catch (e) { toast.error(apiErrorMessage(e) || "Save failed"); }
    finally { setBusy(false); }
  };

  const resetToDefault = async () => {
    const key = `${state}::${selectedStage}`;
    const doc = overrides[key];
    if (!doc?.id) {
      toast.info("Already using the state default");
      return;
    }
    if (!window.confirm("Reset to the state default? Your custom copy will be archived.")) return;
    try {
      await api.delete(`/admin/delinquency/letters/${doc.id}`);
      toast.success("Reset to default");
      await loadOverrides();
    } catch (e) { toast.error(apiErrorMessage(e) || "Reset failed"); }
  };

  const preview = async () => {
    try {
      const resp = await fetch(`${API}/admin/delinquency/letters/preview.pdf`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          property_id: scope, state, stage_key: selectedStage,
          ...previewCtx,
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch (e) { toast.error("Preview failed"); }
  };

  if (!defaults) return <div className="p-6 text-white/50 text-sm">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="letters-page">
      <PageHeader
        eyebrow="Finance"
        title="Delinquency Letter Templates"
        subtitle="Per-state letter cadence for the collection waterfall. Preview the PDF before you commit."
      />

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger className="w-64 h-9 bg-white/[0.04] border-white/10"
                          data-testid="letters-scope-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={state} onValueChange={setState}>
          <SelectTrigger className="w-40 h-9 bg-white/[0.04] border-white/10"
                          data-testid="letters-state-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            {(defaults?.states || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs value={selectedStage} onValueChange={setSelectedStage}>
        <TabsList className="bg-white/[0.03] border border-white/10 flex-wrap h-auto p-1">
          {(defaults?.stages || []).map((s) => (
            <TabsTrigger key={s.key} value={s.key} className="text-xs"
                          data-testid={`letters-tab-${s.key}`}>
              {s.title}
              {overrides[`${state}::${s.key}`] && (
                <span className="ml-1 h-1.5 w-1.5 rounded-full bg-orange-400" />
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {(defaults?.stages || []).map((s) => (
          <TabsContent key={s.key} value={s.key} className="mt-4">
            <Card className="border-white/10 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Scale className="h-4 w-4 text-orange-400" />
                <div className="overline">{s.title} · {state}</div>
                {stageTemplate.overridden ? (
                  <Badge variant="outline" className="border-orange-500/40 text-orange-300 bg-orange-500/10 text-[10px]">
                    Custom
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-white/10 text-white/50 text-[10px]">
                    State default
                  </Badge>
                )}
              </div>

              <div>
                <Label className="text-xs">Subject</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                        className="bg-white/[0.04] border-white/10 mt-1"
                        data-testid="letters-subject" />
              </div>
              <div>
                <Label className="text-xs">Body (supports <code className="text-orange-300">{"{name}"}</code>, <code className="text-orange-300">{"{unit}"}</code>, <code className="text-orange-300">{"{total_due}"}</code>, <code className="text-orange-300">{"{late_fee}"}</code>, <code className="text-orange-300">{"{days_past_due}"}</code>, <code className="text-orange-300">{"{due_date}"}</code>)</Label>
                <Textarea value={body} onChange={(e) => setBody(e.target.value)}
                          rows={14}
                          className="bg-white/[0.04] border-white/10 mt-1 font-mono text-xs"
                          data-testid="letters-body" />
              </div>

              <div className="rounded border border-white/10 bg-white/[0.02] p-3 space-y-2">
                <div className="overline text-[10px]">Preview merge context</div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div>
                    <Label className="text-[10px]">Unit</Label>
                    <Input value={previewCtx.unit} onChange={(e) => setPreviewCtx({ ...previewCtx, unit: e.target.value })}
                            className="bg-white/[0.04] border-white/10 mt-1 h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Resident name</Label>
                    <Input value={previewCtx.resident_name} onChange={(e) => setPreviewCtx({ ...previewCtx, resident_name: e.target.value })}
                            className="bg-white/[0.04] border-white/10 mt-1 h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Total due</Label>
                    <Input type="number" value={previewCtx.total_due} onChange={(e) => setPreviewCtx({ ...previewCtx, total_due: Number(e.target.value) })}
                            className="bg-white/[0.04] border-white/10 mt-1 h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-[10px]">Days past due</Label>
                    <Input type="number" value={previewCtx.days_past_due} onChange={(e) => setPreviewCtx({ ...previewCtx, days_past_due: Number(e.target.value) })}
                            className="bg-white/[0.04] border-white/10 mt-1 h-8 text-xs" />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <Button onClick={save} disabled={busy}
                        className="bg-orange-500 hover:bg-orange-600 rounded-full h-9"
                        data-testid="letters-save-btn">
                  <Save className="h-4 w-4 mr-1" /> Save override
                </Button>
                {stageTemplate.overridden && (
                  <Button variant="outline" onClick={resetToDefault}
                          className="border-white/10 rounded-full h-9"
                          data-testid="letters-reset-btn">
                    <RotateCcw className="h-4 w-4 mr-1" /> Reset to default
                  </Button>
                )}
                <Button variant="outline" onClick={preview}
                        className="border-orange-500/40 text-orange-300 rounded-full h-9 ml-auto"
                        data-testid="letters-preview-btn">
                  <FileDown className="h-4 w-4 mr-1" /> Preview PDF
                </Button>
              </div>
            </Card>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
